import { createRoot } from 'react-dom/client';
import Point from '@arcgis/core/geometry/Point';
import Polygon from '@arcgis/core/geometry/Polygon';
import type { ActionEntity } from './hooks/useScopedActionForm';
import {
  getActionCoordinates,
  getActionGeoJsonGeometry,
} from './utils/actionGeolocation';
import { formatActionTask } from './utils/actionTask';
import { ActionPopupContent } from './ActionPopupContent';
import type { ArcgisMapView } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/arcgisView';

/** Zoom to an action's location and open its ArcGIS popup with task fields. */
export async function openActionMapPopup(params: {
  mapView: ArcgisMapView;
  action: ActionEntity;
}): Promise<void> {
  const { mapView, action } = params;
  const coords = getActionCoordinates(action.geolocation);
  if (!coords || !mapView.popup) return;

  const popupLocation = new Point({
    longitude: coords.longitude,
    latitude: coords.latitude,
  });

  const geoJson = getActionGeoJsonGeometry(action.geolocation);
  const goToTarget =
    geoJson?.type === 'Polygon'
      ? new Polygon({ rings: geoJson.coordinates })
      : geoJson?.type === 'MultiPolygon'
        ? new Polygon({ rings: geoJson.coordinates.flatMap((c) => c) })
        : popupLocation;

  const popupContent = document.createElement('div');
  const root = createRoot(popupContent);
  root.render(<ActionPopupContent action={action} />);

  await mapView.goTo(goToTarget);
  await mapView.openPopup({
    title: formatActionTask(action.name, action.description),
    location: popupLocation,
    content: popupContent,
  });

  const popup = mapView.popup;
  if (!popup) return;

  const handle = popup.watch('visible', (visible) => {
    if (!visible) {
      root.unmount();
      handle.remove();
    }
  });
}
